// filepath: apps/web/src/operator/useOperatorSummary.ts
//
// FR-UI-OPS-1 AC-3 — TanStack Query hook for the cross-tenant
// operator summary.
//
// Backs onto `GET /admin/summary` (FR-OBS-2). The endpoint is
// served by `internal/obs/summary_handler.go` and reads
// `platform.tenant_metrics JOIN platform.tenants` server-side.
//
// WIRE SHAPE (snake_case — matches the Go encoder exactly):
//   {
//     "tenants": [
//       {
//         "tenant_id":            "<uuid>",
//         "slug":                 "<slug>",
//         "instance_count":       <int>,
//         "error_count":          <int>,
//         "active_sandbox_count": <int>
//       },
//       ...
//     ],
//     "totals": {
//       "instance_count":       <int>,
//       "error_count":          <int>,
//       "active_sandbox_count": <int>
//     },
//     "computed_at": "<RFC3339 timestamp>"
//   }
//
// The wire shape is decoded once inside `queryFn` and re-emitted
// as a camelCase `Summary` to the renderer. The private
// `SummaryWireResponse` interface locks the snake_case decode to
// the JSON contract; the `Summary` (camelCase) interface is what
// components consume. This mirrors the `useFieldEvaluations` /
// FR-FORM-4 precedent (`apps/web/src/renderers/form/...`).
//
// CACHE KEY: `['operator', 'summary']`. NO tenant slug — operator
// mode has no tenant context; including a tenant slug would be
// wrong (INV-1 inverse-key norm).
//
// 5-STATE ERROR CONTRACT (FR-UI-CORE-10): 4 active states are
// produced. The 2 N/A states (`stale-version`, `rate-limit`) are
// documented but never produced (no versioned definition applies
// to the platform summary; the endpoint is not rate-limited in
// v1).

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { apiFetch } from "@/api";
import { useOperatorToken } from "./useOperatorToken";

/**
 * Snake_case wire shape — locks the JSON decoder to the exact
 * field names returned by the server. Exported for test access
 * so the TestDesigner-written unit tests can pin the wire
 * contract in isolation.
 *
 * ISS-232 (BA spec amendment, note 17386): the per-tenant object
 * now also carries `name` (server-derived from tenant_slug per
 * BA's spec — v1 has no separate display name), `status`
 * (CASE-derived from enabled + quiesced_at: 'active' | 'disabled'
 * | 'quiesced'), and `tier` (from platform.tenants.tier:
 * 'small' | 'middle' | 'corporate'). These four "always-populated"
 * fields (tenant_id, name, status, tier) are populated for every
 * tenant, even tenants without a metrics row (the LEFT JOIN
 * contract from !1197).
 */
export interface SummaryWireResponse {
  tenants: {
    tenant_id: string;
    name: string;
    status: string;
    tier: string;
    slug: string;
    instance_count: number;
    error_count: number;
    active_sandbox_count: number;
  }[];
  totals: {
    instance_count: number;
    error_count: number;
    active_sandbox_count: number;
  };
  computed_at: string;
}

/**
 * Tenant metric row — camelCase shape exposed to renderers.
 *
 * ISS-232: the four "always-populated" fields (tenantId, name,
 * status, tier) come from `platform.tenants` (LEFT JOIN on
 * tenant_id); the metric counts may be 0 when no metrics row
 * exists for the tenant. `name` is the slug (BA's spec; v1 has
 * no separate display name). `status` is the server-derived
 * CASE result (never "—" — see OperatorTenantsSection adapter).
 */
export interface SummaryTenantRow {
  tenantId: string;
  slug: string;
  name: string;
  // ISS-509 (BA ruling): decoder fallback for a missing status field
  // is "unknown" (machine-readable), NOT "active". Reason from the BA:
  // "a display of invented state is worse than an honest unknown. A
  // disabled tenant must never read 'active'. The operator screen is
  // where trust matters." The UI layer (OperatorTenantsSection /
  // TenantList) renders the localized "Unknown" label via
  // `t("operator.tenants.list.status.unknown", "Unknown")` when the
  // decoded value is exactly "unknown".
  status: "active" | "disabled" | "quiesced" | "unknown";
  // ISS-509 (BA ruling): decoder fallback for a missing tier field
  // is "" (empty string), NOT "small". The UI renders "" as a blank
  // cell — neutral, no invented tier.
  tier: "" | "small" | "middle" | "corporate";
  instanceCount: number;
  errorCount: number;
  activeSandboxCount: number;
}

/**
 * Aggregated totals across all tenants.
 */
export interface SummaryTotals {
  instanceCount: number;
  errorCount: number;
  activeSandboxCount: number;
}

/**
 * Decoded, camelCase summary — what renderers consume.
 */
export interface Summary {
  tenants: SummaryTenantRow[];
  totals: SummaryTotals;
  computedAt: Date;
}

/**
 * 4 active states the hook produces (FR-UI-CORE-10). The 2
 * N/A states (`stale-version`, `rate-limit`) are documented but
 * never produced; the consuming route only branches on the 4
 * active states.
 */
export type OperatorSummaryStatus =
  "loading" | "success" | "fetch-failure" | "401-unauthorized";

/**
 * Custom error envelope. The status code is preserved so the
 * caller can distinguish 401 from 5xx / network-failure.
 */
export interface OperatorSummaryError extends Error {
  status: number;
  isUnauthorized: boolean;
}

function makeError(message: string, status: number): OperatorSummaryError {
  const err = new Error(message) as OperatorSummaryError;
  err.status = status;
  err.isUnauthorized = status === 401;
  return err;
}

/**
 * Status helper — collapse the 4 active states onto a single
 * discriminator. Used by the Summary route to branch rendering.
 */
export function classifyOperatorSummaryStatus(args: {
  isLoading: boolean;
  isSuccess: boolean;
  error: OperatorSummaryError | null;
}): OperatorSummaryStatus {
  if (args.error) {
    if (args.error.isUnauthorized) return "401-unauthorized";
    return "fetch-failure";
  }
  if (args.isLoading) return "loading";
  if (args.isSuccess) return "success";
  return "loading";
}

/**
 * Decode the snake_case wire response into the camelCase Summary
 * type. Defensive: missing fields fall back to 0 / [].
 *
 * ISS-232: the decoder preserves the new `name`, `status`, and
 * `tier` fields so the SPA adapter in OperatorTenantsSection can
 * render them. `status` falls back to "active" (the safe default
 * — never an em-dash) and `tier` falls back to "small" (the
 * smallest tier — never an em-dash, the cast is safe because the
 * tier union is a subset of string).
 */
/**
 * Exported for test access — exposed as a named function so the
 * TestDesigner-written unit tests (useOperatorSummary.test.ts)
 * can verify the decoder + classifier without going through the
 * full MSW fetch interception path. The function is PURE — no
 * side effects, no network access — so exporting it is safe.
 */
export function decodeSummaryWire(wire: unknown): Summary {
  const w = (wire ?? {}) as Partial<SummaryWireResponse>;
  const tenantsRaw = Array.isArray(w.tenants) ? w.tenants : [];
  const tenants: SummaryTenantRow[] = tenantsRaw.map((t) => ({
    tenantId: String(t.tenant_id ?? ""),
    slug: String(t.slug ?? ""),
    name: String(t.name ?? t.slug ?? ""),
    // ISS-509 (BA ruling): absent status → "unknown"; absent tier →
    // "". These are the strictest possible fallbacks — never invent
    // a value the server didn't send.
    status: (t.status === undefined || t.status === null || t.status === ""
      ? "unknown"
      : String(t.status)) as SummaryTenantRow["status"],
    tier: (t.tier === undefined || t.tier === null || t.tier === ""
      ? ""
      : String(t.tier)) as SummaryTenantRow["tier"],
    instanceCount: Number(t.instance_count ?? 0),
    errorCount: Number(t.error_count ?? 0),
    activeSandboxCount: Number(t.active_sandbox_count ?? 0),
  }));
  const t = (w.totals ?? {}) as Partial<SummaryWireResponse["totals"]>;
  const totals: SummaryTotals = {
    instanceCount: Number(t.instance_count ?? 0),
    errorCount: Number(t.error_count ?? 0),
    activeSandboxCount: Number(t.active_sandbox_count ?? 0),
  };
  const computed = w.computed_at ? new Date(w.computed_at) : new Date(NaN);
  return { tenants, totals, computedAt: computed };
}

/**
 * Server base for the operator endpoint. The operator shell is
 * mounted only on the platform apex — the "/admin" path is the
 * server-side route registered by `SummaryHandler.RegisterRoutes`.
 * Same-origin fetch (no CORS).
 */
const OPERATOR_API_BASE = "/admin";

export interface UseOperatorSummaryResult {
  data: Summary | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: OperatorSummaryError | null;
  status: OperatorSummaryStatus;
  refetch: () => Promise<unknown>;
  unauthorized: boolean;
}

/**
 * Read the operator summary. The hook is enabled only when a
 * token is present in the in-memory store — when the user has
 * not yet submitted a token, the gate has not rendered the
 * summary route, so this hook is never called with `token=null`
 * in normal flow. The `enabled` guard is a safety net.
 *
 * On 401 the hook surfaces the failure to the caller; the caller
 * calls `useOperatorToken().clear()` to re-prompt. The hook does
 * NOT clear the token itself (separation of concerns: the hook
 * reports, the gate acts).
 */
export function useOperatorSummary(): UseOperatorSummaryResult {
  const { token } = useOperatorToken();
  const query: UseQueryResult<Summary, OperatorSummaryError> = useQuery<
    Summary,
    OperatorSummaryError
  >({
    queryKey: ["operator", "summary"],
    queryFn: async ({ signal }) => {
      let response: Response;
      try {
        // ISS-377: route through the new `apiFetch` client with the
        // operator-token auth strategy. The client sets
        // `credentials: "omit"` and `Authorization: Bearer <token>`;
        // the OIDC `access_token` is NEVER consulted on this path.
        response = await apiFetch(`${OPERATOR_API_BASE}/summary`, {
          auth: { kind: "operator-token", token },
          init: {
            headers: { Accept: "application/json" },
            signal,
          },
        });
      } catch (networkErr) {
        throw makeError(
          networkErr instanceof Error ? networkErr.message : "network error",
          0,
        );
      }
      if (!response.ok) {
        throw makeError(`HTTP ${response.status}`, response.status);
      }
      const wire = (await response.json()) as unknown;
      return decodeSummaryWire(wire);
    },
    enabled: Boolean(token),
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const status = classifyOperatorSummaryStatus({
    isLoading: query.isLoading,
    isSuccess: query.isSuccess,
    error: query.error,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    status,
    refetch: query.refetch,
    unauthorized: status === "401-unauthorized",
  };
}
